// Organism comparison for cross-organism fragment-search flagging. Kept dependency-free
// (pure string logic, no App/model imports) so the components that use it stay light and
// unit-testable without pulling in the whole application graph.
//
// Two vocabularies collide here: the kinase library's controlled `species`
// (Human / Mouse / Rat) and the public/built-in library's free-text `organism`. Because
// every loaded structure joins Project Targets, one public structure labelled `human` next
// to kinase `Human` would otherwise spuriously flip targetsHaveMixedOrganisms() and
// cross-flag every kinase row. Case/whitespace folding closes that; the alias map folds the
// common<->scientific pairs for the organisms the kinase library actually uses as a
// comparison source, so a source `Human` and a public `Homo sapiens` are the same organism.
//
// Deliberately NOT normalized -- these need judgement, not a fold, and a spurious flag is
// visible rather than silent, so leaving them literal is the safe start:
//   * free-text spelling drift (map-list has ~7 spellings of E. coli; `SARS-CoV-2` vs
//     `SARS-CoV2`) -- an open-ended synonym problem, not attempted here;
//   * multi-organism entries ("Homo sapiens, Mus musculus") -- a complex is neither wholly
//     one organism nor cleanly comparable to a single-organism source.
const ORGANISM_ALIASES = {
    'homo sapiens': 'human',
    'mus musculus': 'mouse',
    'rattus norvegicus': 'rat',
};

/**
 * Fold an organism string to a comparison key: trimmed, lowercased, whitespace-collapsed,
 * then mapped through ORGANISM_ALIASES. For equality/uniqueness checks ONLY -- never for
 * display (callers keep the raw value for labels). '' in yields '' (fail-open).
 * @param {string} organism
 * @returns {string}
 */
export function normalizeOrganism(organism) {
    const key = (organism || '').trim().toLowerCase().replace(/\s+/g, ' ');
    return ORGANISM_ALIASES[key] || key;
}

/**
 * Whether two organisms are a genuine cross-organism pair. Fail-open: if either side is
 * empty (no source atom, or a structure with no organism) it returns false and flags
 * nothing. Compared on the normalized key, so Human/human/Homo sapiens are not a mismatch.
 * @param {string} organism           the structure's organism
 * @param {string} referenceOrganism  the search's source-atom organism
 * @returns {boolean}
 */
export function isCrossOrganism(organism, referenceOrganism) {
    return !!referenceOrganism && !!organism
        && normalizeOrganism(organism) !== normalizeOrganism(referenceOrganism);
}
